// angular imports
import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';

// devextreme imports
import { DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// cc-common imports
import { Utils } from 'cc-common';

// component imports
import { WorkOrderBaseComponent } from '../../_base/work-order-base-component';

// model imports
import { DropDownButtonItemInfo } from '../../_models/dropdown-button-item-info';
import { WorkOrderVendorListResultDto } from '../../_models/work-order-vendor-list-result-dto';
import { BaseWorkOrderParameterDto } from '../../_models/base-work-order-parameter-dto';
import { BaseWorkOrderProposalParameterDto } from '../../_models/base-work-order-proposal-parameter-dto';
import { WorkOrderManageProposalsResultDto } from '../../_models/work-order-manage-proposals-result-dto';
import { WorkOrderProposalResultDto } from '../../_models/work-order-proposal-result-dto';
import { ActionMenuEvent, ActionMenuInfo } from '../../../shared-module/_models/action-menu-info';
import { WorkOrderProposalActionConstants } from '../../_constants/workorder-proposal-action-constants';
import { WorkOrderProposalAddParametersDto } from '../../_models/work-order-proposal-add-parameters-dto';
import { WorkOrderProposalAcceptParametersDto } from '../../_models/work-order-proposal-accept-parameters-dto';
import { VendorProposalQuoteAcceptedEditorResultDto } from '../../_models/vendor-proposal-quote-accepted-editor-result-dto';
import { WorkOrderVendorProposalFileInfoRawDto } from '../../_models/work-order-vendor-proposal-file-info-raw-dto';

// constant imports
import { ActionConstants } from '../../../_constants/action-constants';
import { MessageInterval } from '../../../_constants/messages-intervals';
import { VendorConstants } from '../../_constants/vendor-constants';

@Component({
    selector: 'app-manage-work-order-proposals',
    templateUrl: './manage-work-order-proposals.component.html',
    styleUrls: ['./manage-work-order-proposals.component.scss']
})
export class ManageWorkOrderProposalsComponent extends WorkOrderBaseComponent implements OnInit {
    @ViewChild('manageProposalPopup', { static: false }) manageProposalPopup: DxPopupComponent;

    @Input() communityId: number;
    @Input() customerId: string;
    @Input() workOrderId: number;

    @Output() onClose = new EventEmitter<boolean>();
    @Output() onWorkOrderEditorShow = new EventEmitter<void>();
    @Output() onManageWorkAuthorizationShow = new EventEmitter<void>();

    visible = false;
    isProposalActivity = false;
    showAddVendorForProposal = false;

    selectedVendorForProposal: Array<WorkOrderVendorListResultDto> = [];
    vendorProposalOptions: Array<DropDownButtonItemInfo> = [];

    workOrderTitle: string;
    workOrderProposal: WorkOrderManageProposalsResultDto;
    vendorProposals: Array<WorkOrderProposalResultDto> = [];
    selectedProposal: WorkOrderProposalResultDto;
    actionMenuItems: Array<ActionMenuInfo> = [];

    quotesList: Array<WorkOrderVendorProposalFileInfoRawDto> = [];

    showProposalHistory = false;
    showProposalQuoteManager = false;
    showAcceptPropsalEditor = false;
    showVendorForProposalEditor = false;

    constructor(protected readonly injector: Injector) {
        super(injector);

        this.isActionItemVisible = this.isActionItemVisible.bind(this);
    }

    ngOnInit() {
        this.prepareProposals();
        this.populateProposals();
        this.workOrderTitle = `Manage Vendor Proposals For Work Order: ${this.workOrderId}`;

        this.show();
    }

    show(): void {
        this.visible = true;
    }

    onPopupShown(): void {
        this.manageProposalPopup.instance.repaint();
    }

    onPopupClose(): void {
        this.closeProposals();
    }

    onProposalNotesClick(): void {
        this.onNotesClick();
    }

    onProposalNotesClose(forceReload: boolean): void {
        this.onNotesClose();
        if (Utils.isTrue(forceReload)) {
            this.populateProposals();
        }
    }

    onAddVendorForProposal(): void {
        this.showAddVendorForProposal = true;
    }

    onAddVendorProposalClose(): void {
        this.showAddVendorForProposal = false;
    }

    onVendorSelection(records: Array<WorkOrderVendorListResultDto>): void {
        const vendors: Array<WorkOrderProposalResultDto> = [];
        records.forEach((rec) => {
            let obj: WorkOrderProposalResultDto = new WorkOrderProposalResultDto();
            obj = Object.assign(obj, rec);
            obj.estimatedAmount = 0;
            obj.allowRemoveProposal = true;
            obj.attachmentCount = 0;
            obj.lastGenerated = new Date();
            obj.rpfDownloadUrl = null;
            obj.proposalId = null;
            obj.hasRfpDocument = false;
            obj.proposalVendorId = rec.vendorId;

            if (!this.vendorProposals.find((d) => Utils.equalsIgnoreCase(d.vendorName, rec.vendorName))) {
                vendors.push(obj);
            }
        });
        if (Utils.notNullOrEmpty(vendors)) {
            this.vendorProposals.push(...vendors);
            this.saveProposal();
        }
    }

    onGridContentReady(event: any): void {
        if (Utils.notNullAndDefined(event) && Utils.notNullAndDefined(event.component)) {
            event.component.filter(['crudAction', '<>', ActionConstants.DELETE]);
        }
    }

    onRPFDownloadFile(record: WorkOrderProposalResultDto): void {
        const fileName = `${record.proposalId}_${record.vendorName.replace(' ', '_')}_RFP.pdf`;
        const baseParams: BaseWorkOrderProposalParameterDto = new BaseWorkOrderProposalParameterDto();
        baseParams.communityId = this.communityId;
        baseParams.workOrderId = this.workOrderId;
        baseParams.workOrderProposalId = record.proposalId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.rfpDownload(baseParams).subscribe(
            (fileData) => {
                this.helperService.showBlobInBrowserTab(fileData, fileName);
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    onEmailClick(record: WorkOrderProposalResultDto): void {
        this.emailSharedService.emailComposerForWorkOrder(this.communityId, this.workOrderId, record.proposalId);
    }

    onGridAttachmentClick(record: WorkOrderProposalResultDto): void {
        this.selectedProposal = record;
        this.showProposalQuoteManager = true;
    }

    onQuoteManagerClose(): void {
        this.showProposalQuoteManager = false;
        this.populateProposals();
    }

    onProposalHistoryClick(record: WorkOrderProposalResultDto): void {
        this.selectedProposal = record;
        this.showProposalHistory = true;
    }

    onProposalHistoryClose(): void {
        this.showProposalHistory = false;
    }

    onRegenerateProposalRequest(): void {
        const baseParams: BaseWorkOrderParameterDto = new BaseWorkOrderParameterDto();
        baseParams.communityId = this.communityId;
        baseParams.workOrderId = this.workOrderId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.regenerateProposalRequest(baseParams).subscribe(
            (result: any) => {
                this.workOrderHelperService.hideLoading();
                this.isProposalActivity = true;
                notify('Proposal request re-generated successfully.', 'Success', MessageInterval.successMsgInterval);
                this.populateProposals();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    onEditWorkOrderClick(): void {
        this.onWorkOrderEditorShow.emit();
        this.closeProposals();
    }

    onManageWorkAuthorizationClick(): void {
        this.onManageWorkAuthorizationShow.emit();
        this.closeProposals();
    }

    isActionItemVisible(item: ActionMenuInfo, record: WorkOrderProposalResultDto): boolean {
        let isItemVisible = true;
        if (Utils.equalsIgnoreCase(item.action, WorkOrderProposalActionConstants.REMOVE)) {
            isItemVisible = record.allowRemoveProposal;
        }

        return isItemVisible;
    }

    onActionItemClick(event: ActionMenuEvent<WorkOrderProposalResultDto>): void {
        this.selectedProposal = event.record;
        const actionItem = event.item.action;

        if (Utils.equalsIgnoreCase(actionItem, WorkOrderProposalActionConstants.EDIT)) {
            this.editProposal();
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderProposalActionConstants.ACCEPT)) {
            this.populateProposalAcceptedEditor();
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderProposalActionConstants.REMOVE)) {
            this.deleteProposal();
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderProposalActionConstants.GENERATE_RFP)) {
            this.generateRFP();
        }
    }

    onAcceptProposalEditorClose(): void {
        this.showAcceptPropsalEditor = false;
        this.populateProposals();
    }

    onVendorForProposalEditorClose(): void {
        this.showVendorForProposalEditor = false;
        setTimeout(() => this.populateProposals(), 500);
    }

    private saveProposal(): void {
        const baseParam: WorkOrderProposalAddParametersDto = new WorkOrderProposalAddParametersDto();
        baseParam.workOrderId = this.workOrderId;
        baseParam.communityId = this.communityId;
        baseParam.communityVendorIds = [];
        baseParam.locationVendorIds = [];
        baseParam.temporaryVendorIds = [];

        this.vendorProposals.forEach((vendorProposal) => {
            if (Utils.notNullAndDefined(vendorProposal.proposalVendorType)) {
                switch (vendorProposal.proposalVendorType) {
                    case VendorConstants.COMMUNITY_VENDOR:
                        baseParam.communityVendorIds.push(vendorProposal.proposalVendorId);
                        break;
                    case VendorConstants.ALL_VENDOR:
                        baseParam.locationVendorIds.push(vendorProposal.proposalVendorId);
                        break;
                    case VendorConstants.TEMP_VENDOR:
                        baseParam.temporaryVendorIds.push(vendorProposal.proposalVendorId);
                        break;
                }
            }
        });

        this.workOrderHelperService.showLoading();
        this.workOrderService.saveWorkOrderProposal(baseParam).subscribe(
            () => {
                notify('Work Order Proposal saved successfully.', 'Success', MessageInterval.successMsgInterval);
                this.workOrderHelperService.hideLoading();
                this.isProposalActivity = true;
                this.populateProposals();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private populateProposals(): void {
        const baseParam: BaseWorkOrderParameterDto = new BaseWorkOrderParameterDto();
        baseParam.communityId = this.communityId;
        baseParam.workOrderId = this.workOrderId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.manageProposals(baseParam).subscribe(
            (result: WorkOrderManageProposalsResultDto) => {
                this.workOrderProposal = result;
                this.vendorProposals = result.workOrderProposals;
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
                this.closeProposals();
            }
        );
    }

    private prepareProposals(): void {
        this.actionMenuItems = [
            {
                name: 'Edit',
                icon: this.iconEdit,
                action: WorkOrderProposalActionConstants.EDIT,
                disabled: false,
                parent: null,
                items: []
            },
            {
                name: 'Remove',
                icon: this.iconDelete,
                action: WorkOrderProposalActionConstants.REMOVE,
                disabled: false,
                parent: null,
                items: []
            },
            {
                name: 'Generate RFP',
                icon: this.iconFile,
                action: WorkOrderProposalActionConstants.GENERATE_RFP,
                disabled: false,
                parent: null,
                items: []
            },
            {
                name: 'Accept',
                icon: this.iconCheck,
                action: WorkOrderProposalActionConstants.ACCEPT,
                disabled: false,
                parent: null,
                items: []
            }
        ];
    }

    private deleteProposal(): void {
        const baseParams: BaseWorkOrderProposalParameterDto = new BaseWorkOrderProposalParameterDto();
        baseParams.communityId = this.communityId;
        baseParams.workOrderId = this.workOrderId;
        baseParams.workOrderProposalId = this.selectedProposal.proposalId;

        this.workOrderService.deleteWorkOrderProposal(baseParams).subscribe(
            (result: any) => {
                this.isProposalActivity = true;
                this.populateProposals();
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private generateRFP(): void {
        const baseParams: BaseWorkOrderProposalParameterDto = new BaseWorkOrderProposalParameterDto();
        baseParams.workOrderProposalId = this.selectedProposal.proposalId;
        baseParams.communityId = this.communityId;
        baseParams.workOrderId = this.workOrderId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.generateProposalRequest(baseParams).subscribe(
            () => {
                this.isProposalActivity = true;
                notify('RFP generated successfully.', 'Success', MessageInterval.successMsgInterval);
                this.populateProposals();
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private editProposal(): void {
        this.showVendorForProposalEditor = true;
    }

    private populateProposalAcceptedEditor(): void {
        const baseParams: BaseWorkOrderProposalParameterDto = new BaseWorkOrderProposalParameterDto();
        baseParams.communityId = this.communityId;
        baseParams.workOrderId = this.workOrderId;
        baseParams.workOrderProposalId = this.selectedProposal.proposalId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.getQuoteAcceptedEditor(baseParams).subscribe(
            (result: VendorProposalQuoteAcceptedEditorResultDto) => {
                this.quotesList = result.vendorProposalQuotesAccespted;
                if (this.quotesList.length > 1) {
                    this.showAcceptPropsalEditor = true;
                } else {
                    this.acceptProposal();
                }
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private acceptProposal(): void {
        const baseParam: WorkOrderProposalAcceptParametersDto = new WorkOrderProposalAcceptParametersDto();
        baseParam.communityId = this.communityId;
        baseParam.workOrderId = this.workOrderId;
        baseParam.workOrderProposalId = this.selectedProposal.proposalId;
        baseParam.acceptedFileIds = Utils.notNullOrEmpty(this.quotesList) ? this.quotesList.map((d) => d.fileId) : [];

        this.workOrderHelperService.showLoading();
        this.workOrderService.acceptWorkOrderProposal(baseParam).subscribe(
            (result: any) => {
                notify('Vendor proposal accepted.', 'Success', MessageInterval.successMsgInterval);
                this.populateProposals();
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private closeProposals(): void {
        this.visible = false;
        this.onClose.emit(this.isProposalActivity);
    }
}
